import { useQuery } from "@tanstack/react-query";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
} from "recharts";
import { IndianRupee, ShoppingBag, Star, TrendingUp, Loader2, Download } from "lucide-react";
import { toast } from "sonner";
import { getAnalytics, getOrders, getEarningsEntries, errMsg } from "@/lib/api";
import { inr0 } from "@/lib/format";
import { toCsv, downloadCsv, todayStamp } from "@/lib/csv";
import { Button } from "@/components/ui/button";

const KpiCard = ({ icon: Icon, label, value, testid, accent }) => (
  <div className="rounded-md border border-border bg-card p-5" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="text-xs uppercase tracking-[0.12em] text-muted-foreground font-semibold">
        {label}
      </span>
      <span className={`h-8 w-8 rounded-md grid place-items-center ${accent}`}>
        <Icon className="h-4 w-4" />
      </span>
    </div>
    <p className="font-display font-black text-3xl sm:text-4xl mt-3 leading-none">{value}</p>
  </div>
);

const ChartCard = ({ title, subtitle, children }) => (
  <div className="rounded-md border border-border bg-card p-5">
    <div className="mb-4">
      <h3 className="font-display font-bold text-lg leading-tight">{title}</h3>
      {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
    </div>
    {children}
  </div>
);

const GREEN = "#16A34A";

export default function Analytics() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["vendor-analytics"],
    queryFn: async () => (await getAnalytics()).data,
    refetchInterval: 60000,
  });

  const exportOrders = async () => {
    try {
      const { data: rows } = await getOrders({ limit: 1000 });
      const cols = [
        { label: "Order ID", get: (o) => o.id },
        { label: "Date", get: (o) => o.created_at },
        { label: "Status", get: (o) => o.status },
        { label: "Items", get: (o) => (o.items || []).map((i) => `${i.quantity || 1}x ${i.name}`).join("; ") },
        { label: "Subtotal", get: (o) => o.subtotal },
        { label: "Delivery Fee", get: (o) => o.delivery_fee },
        { label: "Taxes", get: (o) => (o.gst_food || 0) + (o.gst_service || 0) },
        { label: "Total", get: (o) => o.total ?? o.gross_total },
        { label: "Payment Method", get: (o) => o.payment_method },
        { label: "Payment Status", get: (o) => o.payment_status },
        { label: "Your Payout", get: (o) => o.vendor_payout },
      ];
      downloadCsv(`orders_${todayStamp()}.csv`, toCsv(rows, cols));
      toast.success(`Exported ${(rows || []).length} orders`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const exportPayouts = async () => {
    try {
      const { data: rows } = await getEarningsEntries();
      const cols = [
        { label: "Entry ID", get: (e) => e.id },
        { label: "Order ID", get: (e) => e.order_id },
        { label: "Date", get: (e) => e.created_at },
        { label: "Amount", get: (e) => e.amount },
        { label: "Status", get: (e) => e.status },
        { label: "Restaurant", get: (e) => e.order_info?.restaurant_name },
      ];
      downloadCsv(`payouts_${todayStamp()}.csv`, toCsv(rows, cols));
      toast.success(`Exported ${(rows || []).length} payout entries`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (isLoading) {
    return (
      <div className="h-screen grid place-items-center text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }
  if (isError) {
    return <div className="p-8 text-destructive">{errMsg(error)}</div>;
  }

  const kpis = data?.kpis || {};
  const daily = (data?.daily || []).map((d) => ({
    ...d,
    label: new Date(d.date).toLocaleDateString("en-IN", { day: "numeric", month: "short" }),
  }));
  const hours = (data?.hours || []).map((h) => ({
    ...h,
    label: `${String(h.hour).padStart(2, "0")}h`,
  }));
  const maxHour = Math.max(1, ...hours.map((h) => h.orders));

  return (
    <div className="p-5 sm:p-8 max-w-6xl">
      <div className="mb-7 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-display font-black text-3xl leading-none">Analytics</h1>
          <p className="text-sm text-muted-foreground mt-1.5">
            {data?.shop_name} · last {data?.range_days || 14} days
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportOrders} data-testid="export-orders-btn" className="font-semibold">
            <Download className="h-4 w-4 mr-1.5" /> Orders CSV
          </Button>
          <Button variant="outline" size="sm" onClick={exportPayouts} data-testid="export-payouts-btn" className="font-semibold">
            <Download className="h-4 w-4 mr-1.5" /> Payouts CSV
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard
          icon={ShoppingBag}
          label="Orders"
          value={kpis.orders ?? 0}
          testid="kpi-orders"
          accent="bg-blue-100 text-blue-700"
        />
        <KpiCard
          icon={IndianRupee}
          label="Revenue"
          value={inr0(kpis.revenue)}
          testid="kpi-revenue"
          accent="bg-emerald-100 text-emerald-700"
        />
        <KpiCard
          icon={Star}
          label="Avg rating"
          value={(kpis.avg_rating ?? 0).toFixed(1)}
          testid="kpi-rating"
          accent="bg-amber-100 text-amber-700"
        />
        <KpiCard
          icon={TrendingUp}
          label="Ratings"
          value={kpis.rating_count ?? 0}
          testid="kpi-rating-count"
          accent="bg-violet-100 text-violet-700"
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <ChartCard title="Daily revenue" subtitle="Revenue per day">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={daily} margin={{ top: 8, right: 4, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <Tooltip
                cursor={{ fill: "hsl(var(--muted))" }}
                formatter={(v) => [inr0(v), "Revenue"]}
                contentStyle={{ borderRadius: 8, border: "1px solid hsl(var(--border))", fontSize: 13 }}
              />
              <Bar dataKey="revenue" fill={GREEN} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Orders by hour" subtitle="When your customers order">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={hours} margin={{ top: 8, right: 4, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 9 }} interval={1} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} stroke="hsl(var(--muted-foreground))" />
              <Tooltip
                cursor={{ fill: "hsl(var(--muted))" }}
                formatter={(v) => [v, "Orders"]}
                contentStyle={{ borderRadius: 8, border: "1px solid hsl(var(--border))", fontSize: 13 }}
              />
              <Bar dataKey="orders" radius={[4, 4, 0, 0]}>
                {hours.map((h) => (
                  <Cell key={h.hour} fill={h.orders === maxHour ? "#F59E0B" : GREEN} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}
